/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Suspense, useState } from 'react';

import { RouteError } from '@/components/RouteError';
import { createQueryClient } from '@/lib/queryClient';
import { routeTree } from '@/routes';

/**
 * The application.
 *
 * The router and the query client are built once, in state, so a re-render does
 * not discard the cache or reset the route.
 */
function buildRouter(queryClient: ReturnType<typeof createQueryClient>) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Every route is code-split, so there is always a moment before a screen
    // arrives. A blank frame is better than a spinner that flashes.
    defaultPendingComponent: () => <div className="wrap" />,
    // A screen that could not be opened: loaded again once when Vellum was
    // updated under an open tab, otherwise the ordinary error state.
    defaultErrorComponent: RouteError,
  });
}

// Typed routes: a bad `to` or a missing search param fails the build (FE-1).
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}

/**
 * The client and the router, built together.
 *
 * Each needs the other: the router takes the client as context, and the client
 * sends somebody to sign in, through the router, when it finds their session
 * over -- with where they were, so signing in brings them back.
 */
function build() {
  // Filled in a line below; the callback runs long after, on a request.
  const late: { router?: ReturnType<typeof buildRouter> } = {};
  const queryClient = createQueryClient({
    onSessionEnded: async () => {
      const { router } = late;
      if (!router) return;
      // The latest location, not the settled one: a 401 met while a route is
      // still loading belongs to where the person was going.
      const { pathname, href } = router.latestLocation;
      if (pathname === '/signin') return;
      await router.navigate({ to: '/signin', search: { next: href } });
    },
  });
  const router = buildRouter(queryClient);
  late.router = router;
  return { queryClient, router };
}

export function App() {
  const [{ queryClient, router }] = useState(build);

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<div className="wrap" />}>
        <RouterProvider router={router} />
      </Suspense>
    </QueryClientProvider>
  );
}
